import { IconButton, ListItemIcon, ListItemText, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { EXPERIENCE_PRIMARY_FILTER, TAB_STATUSES } from "../../../../constants";
import { useUrlParams } from "../../../../hooks";
import { EyeClosed, EyeNormal, HumBurgurMenu } from "../../../../icons";
import { updateVisualBuilderData } from "../../../../store/actions";
import renderTypeIcon from "../../../../utils/renderTypeIcon";

const useStyles = makeStyles((theme) => ({
  row: {
    position: "relative",
    padding: "0 0 0 15px",
    display: "flex",
    alignItems: "center",
    height: 42,
    width: "100%",
    cursor: "pointer",
    borderWidth: 0,
  },
  hideIcon: {
    position: "relative",
    marginLeft: "0px",
    top: "1px",
    fontSize: "1.6rem",
    stroke: "#FFF",
    fill: "none",
  },
  onlyRows: {
    display: "flex",
    "flex-direction": "row",
    alignItems: "center",
    width: "100%",
    "max-width": "240px",
    "& .MuiListItemIcon-root": {
      justifyContent: "center",
    },
    "& > svg": {
      visibility: "hidden",
    },
    "@media (min-width:767px)": {
      "&:hover": {
        "& > svg": {
          visibility: (props) =>
            props.tabStatus === TAB_STATUSES.CUSTOMIZE && !props.contentType.hidden ? "inherit" : "hidden",
        },
      },
    },
  },
  icon: {
    fontSize: "1.5rem",
    stroke: "#FFF",
    fill: "none",
  },
  eyeClosed: {
    opacity: ".5",
    fontSize: "1.6rem",
  },
  whiteText: {
    color: theme.palette.common.white,
    fontSize: "16px",
    textAlign: "left",
  },
  active: {
    color: theme.primaryColor,
  },
  cellActive: {
    color: `${theme.primaryColor} !important`,
  },
  inactive: {
    opacity: 0.5,
  },
  hamburger: {
    fontSize: ".75rem !important",
  },
}));

export default function ContentTypeItem(props) {
  const {
    contentType,
    filteredContentTypeMediaItems = {},
    gridView,
    index,
    locked,
    onChangeTypeStatus,
    primaryFilter,
    snapshotDraggable = {},
    tabStatus,
    tagFilterId,
    typeFilterId,
  } = props;

  const classes = useStyles(props);
  const theme = useTheme();
  const params = useUrlParams();

  const [hideShowIcons, setHideShowIcons] = useState(false);

  useEffect(() => {
    setHideShowIcons(contentType.hidden);
  }, [contentType.hidden]);

  const dispatch = useDispatch();

  const statusSelected = typeFilterId === contentType.id;

  const iconProps = {
    className: classes.icon,
    htmlColor: statusSelected ? theme.primaryColor : "#fff",
  };

  const toggleType = () => {
    const values = { typeFilterId: typeFilterId === contentType.id ? null : contentType.id };

    if (primaryFilter === EXPERIENCE_PRIMARY_FILTER.TYPE && values.typeFilterId) {
      values.tagFilterId = null;
    }

    if (values.typeFilterId && !gridView) {
      let initialMediaItem;
      if (primaryFilter === EXPERIENCE_PRIMARY_FILTER.TAG && tagFilterId) {
        initialMediaItem = filteredContentTypeMediaItems[values.typeFilterId].find((mediaItem) =>
          mediaItem.tags.includes(tagFilterId),
        );
      } else {
        initialMediaItem = filteredContentTypeMediaItems[values.typeFilterId][0];
      }

      if (initialMediaItem) {
        values.currentMediaItemId = initialMediaItem.id;
      }
    }

    dispatch(updateVisualBuilderData(params, values, {}, true));
  };

  const HideShowButton = () => (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onChangeTypeStatus(contentType, index);
      }}
      disabled={locked}
    >
      {contentType.hidden ? (
        <EyeClosed className={`${classes.hideIcon} ${classes.eyeClosed}`} />
      ) : (
        <EyeNormal className={classes.hideIcon} />
      )}
    </IconButton>
  );

  return (
    <button
      type="button"
      tabIndex={0}
      className={`${classes.row} ${statusSelected && classes.cellActive} nospace`}
      onMouseEnter={() => setHideShowIcons(true)}
      onMouseLeave={() => setHideShowIcons(false)}
      onClick={toggleType}
      style={{
        backgroundColor: snapshotDraggable.isDragging || typeFilterId === contentType.value ? "#000" : "inherit",
        width: "240px",
      }}
      data-testid="content-type-container"
    >
      <div className={classes.onlyRows}>
        {tabStatus === TAB_STATUSES.CUSTOMIZE && (
          <HumBurgurMenu data-testid="content-type-menu-icon" className={classes.hamburger} htmlColor="#d1d1d1" />
        )}
        <ListItemIcon className={classes.listItemIcon} data-testid="content-type-icon-container">
          {renderTypeIcon(contentType.value, iconProps)}
        </ListItemIcon>
        <ListItemText
          primary={contentType.name}
          className={`${
            contentType.hidden && tabStatus === TAB_STATUSES.CUSTOMIZE
              ? `${classes.whiteText} ${classes.inactive}`
              : classes.whiteText
          } ${statusSelected ? classes.active : ""}`}
          data-testid="content-type-name-container"
        />
        {tabStatus === TAB_STATUSES.CUSTOMIZE && (
          <div style={{ visibility: hideShowIcons ? "visible" : "hidden" }} data-testid="content-type-visibility-icon">
            <HideShowButton />
          </div>
        )}
      </div>
    </button>
  );
}
